import React from "react";
import { Play, Video } from "lucide-react";

/**
 * ProfileTVHeader — the big TV-frame Play button hero block. Pure
 * presentational; the parent owns `isPlaying`, `currentVideoIndex`,
 * and the `setIsPlaying`/`setCurrentVideoIndex` plumbing.
 *
 * Props:
 *   profile          — { username }  (only username is read here)
 *   playableVideos   — array of videos currently visible after region filter
 *   activeRegion     — current region tab ('Global' | 'Americas' | …)
 *   onPlayAll        — invoked when user taps the TV / Play button
 */
export const ProfileTVHeader = ({ profile, playableVideos, activeRegion, onPlayAll }) => (
  <>
    {/* Channel Branding - Stacked */}
    <div className="text-center mb-6">
      <p className="text-gray-400 text-lg sm:text-xl tracking-wider" data-testid="profile-url">GlobalEyes.TV/</p>
      <h1
        className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-emerald-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent"
        data-testid="profile-username"
      >
        {profile.username}
      </h1>
    </div>

    {/* TV Display with Play Button */}
    <div className="flex justify-center mb-8">
      <div className="relative">
        {/* TV Frame */}
        <div className="relative bg-gray-900 rounded-lg border-4 border-gray-700 shadow-2xl shadow-black/50 p-2">
          {/* TV Screen */}
          <div
            className="relative bg-gradient-to-br from-gray-800 via-gray-900 to-black rounded w-72 h-44 sm:w-96 sm:h-56 md:w-[500px] md:h-72 flex items-center justify-center cursor-pointer group overflow-hidden"
            onClick={onPlayAll}
            data-testid="profile-tv-screen"
          >
            {/* Screen Reflection Effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent" />
            {/* Scanlines Effect */}
            <div
              className="absolute inset-0 opacity-5"
              style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.3) 4px)" }}
            />

            {playableVideos.length > 0 ? (
              <div className="relative z-10 flex flex-col items-center py-3">
                {/* Live Indicator */}
                <div className="flex items-center gap-2 bg-black/50 px-2 py-0.5 rounded-full border border-red-500/30 mb-2">
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-gray-300 text-[10px] font-medium">LIVE</span>
                </div>
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-emerald-600/90 group-hover:bg-emerald-500 transition-all flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:scale-110">
                  <Play className="h-6 w-6 sm:h-8 sm:w-8 text-white ml-0.5" fill="white" />
                </div>
                <p className="text-white font-semibold mt-1.5 text-xs sm:text-sm">Play Featured</p>
                <p className="text-gray-400 text-[10px] sm:text-xs">
                  {playableVideos.length} featured broadcast{playableVideos.length !== 1 ? "s" : ""}
                  {activeRegion !== "Global" ? ` · ${activeRegion}` : ""}
                </p>
                <p className="text-emerald-400/90 text-[10px] sm:text-xs font-bold tracking-wider mt-2">MADE BY HUMANS</p>
                <p className="text-emerald-400/90 text-[10px] sm:text-xs font-bold tracking-wider">NOT AI</p>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-3">
                <Video className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p className="text-xs">
                  {activeRegion === "Global" ? "No broadcasts yet" : `No broadcasts in ${activeRegion}`}
                </p>
              </div>
            )}
          </div>

          {/* TV Brand */}
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-gray-700 px-4 py-0.5 rounded-b text-[10px] text-gray-400 font-medium tracking-widest">
            GlobalEyes.TV
          </div>
        </div>

        {/* TV Stand */}
        <div className="flex justify-center">
          <div className="w-20 h-3 bg-gray-700 rounded-b-lg" />
        </div>
        <div className="flex justify-center -mt-0.5">
          <div className="w-32 h-2 bg-gray-800 rounded-full" />
        </div>
      </div>
    </div>
  </>
);
